/**
 * The facts contract, version 1: the schema rules and the value rules, step by step as
 * `docs/facts-schema.md` gives them. Build round 18 (2026-10-07), decision 0503.
 *
 * The database is the authority (`private.site_facts_schema_problems` and
 * `private.site_facts_value_problems`, migration `20261007500001`). This file repeats the same
 * algorithm so the browser can answer before a save, and `tests/facts-contract.test.ts` holds it and
 * the database to the shared corpus in `tests/fixtures/facts/corpus.json`. A rule changes in the
 * migration, here, in the doc and in the corpus together, or the two validators drift apart.
 *
 * Pure and dependency-free: no DOM, no Node API, no network.
 */
import {
  OPENING_DAYS,
  type FactsField,
  type FactsFieldType,
  type FactsItemField,
  type FactsItemFieldType,
  type FactsPriceOption,
  type FactsProblem,
  type FactsSchema
} from "./types";

/** Every code `validateFactsSchema` can return, as `docs/facts-schema.md` lists them. */
export const FACTS_SCHEMA_PROBLEM_CODES = [
  "schema_not_object",
  "unknown_property",
  "version_unsupported",
  "fields_missing",
  "fields_empty",
  "field_not_object",
  "key_invalid",
  "key_duplicate",
  "label_invalid",
  "help_invalid",
  "type_unknown",
  "type_reserved",
  "item_type_not_allowed",
  "editable_invalid",
  "required_invalid",
  "locked_type_editable",
  "max_length_invalid",
  "allowed_values_invalid",
  "min_max_invalid",
  "integer_invalid",
  "price_max_invalid",
  "price_option_invalid",
  "item_fields_invalid",
  "items_bounds_invalid"
] as const;

/** Every code `validateFactsValues` can return, as `docs/facts-schema.md` lists them. */
export const FACTS_VALUE_PROBLEM_CODES = [
  "values_not_object",
  "unknown_key",
  "required",
  "wrong_type",
  "control_character",
  "blank",
  "too_long",
  "not_allowed_value",
  "not_integer",
  "out_of_range",
  "too_many_decimals",
  "price_fixed",
  "opening_hours_invalid",
  "time_invalid",
  "time_order",
  "phone_invalid",
  "email_invalid",
  "url_invalid",
  "too_few_items",
  "too_many_items"
] as const;

export type FactsSchemaProblemCode = (typeof FACTS_SCHEMA_PROBLEM_CODES)[number];
export type FactsValueProblemCode = (typeof FACTS_VALUE_PROBLEM_CODES)[number];

const KEY_PATTERN = /^[a-z][a-z0-9_]{0,47}$/;
const TIME_PATTERN = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
const PHONE_PATTERN = /^\+46[1-9][0-9]{6,8}$/;
const EMAIL_PATTERN =
  /^[A-Za-z0-9_%+-]+(\.[A-Za-z0-9_%+-]+)*@([A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;
// Lowercase `https://` only. `@` cannot come before the first `/`, `?` or `#`, so no credentials;
// no space, quote, `<`, `>` or backslash anywhere; `%` only as a %XX escape.
const URL_PATTERN =
  /^https:\/\/(([A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)(:[0-9]{1,5})?([/?#]([A-Za-z0-9._~:/?#@!$&'()*+,;=[\]-]|%[0-9A-Fa-f]{2})*)?$/;

const LABEL_MAX = 80;
const HELP_MAX = 200;
const TEXT_MAX_LENGTH = 500;
const NUMBER_BOUND = 1e12;
const PRICE_MAX = 1e9;
const LIST_MAX_ITEMS = 30;
const EMAIL_MAX = 254;
const EMAIL_LOCAL_MAX = 64;
const URL_MAX = 300;

const FIELD_TYPES: readonly FactsFieldType[] = [
  "text",
  "number",
  "price",
  "opening_hours",
  "phone",
  "email",
  "url",
  "list"
];
const ITEM_FIELD_TYPES: readonly FactsItemFieldType[] = ["text", "number", "price", "phone", "email", "url"];
/** Decision 0503 locks these: only Petrovic changes them, so a schema may never mark them editable. */
const LOCKED_TYPES: readonly string[] = ["phone", "email", "url"];

const COMMON_PROPERTIES = ["key", "label", "type", "required", "help"];
const TYPE_PROPERTIES: Record<FactsFieldType, readonly string[]> = {
  text: ["max_length", "allowed_values"],
  number: ["min", "max", "integer"],
  price: ["max", "from", "incl_vat"],
  opening_hours: [],
  phone: [],
  email: [],
  url: [],
  list: ["item_fields", "min_items", "max_items"]
};

type JsonObject = Record<string, unknown>;
type AnyField = FactsField | FactsItemField;

/** A set of problems in the order found; the same `{key, code}` pair is kept once. */
class ProblemList {
  private readonly seen = new Set<string>();
  readonly problems: FactsProblem[] = [];

  add(key: string, code: FactsSchemaProblemCode | FactsValueProblemCode): void {
    const id = JSON.stringify([key, code]);
    if (this.seen.has(id)) return;
    this.seen.add(id);
    this.problems.push({ key, code });
  }
}

function isObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * An own property's value, or undefined. A field or a facts document keyed `constructor` must not
 * find `Object.prototype.constructor` where the document has nothing.
 */
function own(record: JsonObject, name: string): unknown {
  return Object.hasOwn(record, name) ? record[name] : undefined;
}

/** Characters as the contract counts them: Unicode code points, so an emoji is one, not two. */
export function codePointLength(value: string): number {
  return Array.from(value).length;
}

/** Empty, or only U+0020 spaces. Any other character, a tab or a no-break space included, is not blank. */
export function isBlank(value: string): boolean {
  return /^ *$/.test(value);
}

/** U+0000 to U+001F or U+007F. Checked by code unit, since every one of them is a single unit. */
function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const unit = value.charCodeAt(index);
    if (unit <= 0x1f || unit === 0x7f) return true;
  }
  return false;
}

/** A JSON number with no fractional part; 5.0 parses to 5 and counts. */
function isWholeNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value);
}

/**
 * At most two decimals: the contract's `Math.round(x * 100) / 100 === x`, which PostgreSQL says as
 * `v = round(v, 2)`. A whole number is answered first because `x * 100` loses precision long before
 * a double stops holding whole numbers, and PostgreSQL's numeric never does.
 */
function hasAtMostTwoDecimals(value: number): boolean {
  return Number.isInteger(value) || Math.round(value * 100) / 100 === value;
}

/** A label or a help text: plain text on one line, not blank, no `<` or `>`, at most `max` characters. */
function isPlainLabel(value: unknown, max: number): boolean {
  return (
    typeof value === "string" &&
    !isBlank(value) &&
    codePointLength(value) <= max &&
    !hasControlCharacter(value) &&
    !value.includes("<") &&
    !value.includes(">")
  );
}

function isFieldType(value: unknown): value is FactsFieldType {
  return typeof value === "string" && (FIELD_TYPES as readonly string[]).includes(value);
}

function isItemFieldType(value: FactsFieldType): value is FactsItemFieldType {
  return (ITEM_FIELD_TYPES as readonly string[]).includes(value);
}

function isLockedType(value: unknown): boolean {
  return typeof value === "string" && LOCKED_TYPES.includes(value);
}

/** A non-empty array of distinct strings, each not blank, on one line, and within `maxLength` when known. */
function isValidAllowedValues(value: unknown, maxLength: number | null): boolean {
  if (!Array.isArray(value) || value.length === 0) return false;
  const seen = new Set<string>();
  for (const entry of value) {
    if (typeof entry !== "string" || isBlank(entry) || hasControlCharacter(entry) || seen.has(entry)) return false;
    if (maxLength !== null && codePointLength(entry) > maxLength) return false;
    seen.add(entry);
  }
  return true;
}

function isBound(value: unknown): value is number {
  return typeof value === "number" && Math.abs(value) <= NUMBER_BOUND;
}

/** Exactly `{ "editable": true }`, or exactly `{ "editable": false, "value": <boolean> }`. */
function isPriceOption(value: unknown): boolean {
  if (!isObject(value)) return false;
  const names = Object.keys(value);
  const editable = own(value, "editable");
  if (editable === true) return names.length === 1;
  return editable === false && names.length === 2 && typeof own(value, "value") === "boolean";
}

/**
 * One field, top-level or inside a list ("Schema problem codes", step 5). `slot` is its positional path
 * (`fields[i]` or `<list path>.item_fields[j]`), used when it has no usable key; `keyPath` turns a
 * valid key into its path. `siblingKeys` collects the keys already seen at this level.
 */
function checkField(
  field: unknown,
  slot: string,
  keyPath: (key: string) => string,
  inList: boolean,
  siblingKeys: Set<string>,
  problems: ProblemList
): void {
  if (!isObject(field)) {
    problems.add(slot, "field_not_object");
    return;
  }

  const key = own(field, "key");
  const keyValid = typeof key === "string" && KEY_PATTERN.test(key);
  const path = keyValid ? keyPath(key) : slot;
  if (!keyValid) problems.add(slot, "key_invalid");
  else if (siblingKeys.has(key)) problems.add(path, "key_duplicate");
  else siblingKeys.add(key);

  if (!isPlainLabel(own(field, "label"), LABEL_MAX)) problems.add(path, "label_invalid");
  // `help` is optional, but a present `help`, `null` included, must be a valid text.
  if (Object.hasOwn(field, "help") && !isPlainLabel(field.help, HELP_MAX)) problems.add(path, "help_invalid");

  const rawType = own(field, "type");
  let type: FactsFieldType | null = null;
  if (rawType === "image") problems.add(path, "type_reserved");
  else if (!isFieldType(rawType)) problems.add(path, "type_unknown");
  else if (inList && !isItemFieldType(rawType)) problems.add(path, "item_type_not_allowed");
  else type = rawType;

  // An item field has no `editable` of its own: the list's decides, and one written there is an
  // unknown property below.
  if (!inList && typeof own(field, "editable") !== "boolean") problems.add(path, "editable_invalid");
  if (typeof own(field, "required") !== "boolean") problems.add(path, "required_invalid");

  // The type decides which properties exist, so nothing more can be said without a valid one.
  if (type === null) return;

  const allowed = new Set([...COMMON_PROPERTIES, ...(inList ? [] : ["editable"]), ...TYPE_PROPERTIES[type]]);
  if (Object.keys(field).some((name) => !allowed.has(name))) problems.add(path, "unknown_property");

  if (!inList && isLockedType(type) && own(field, "editable") === true) problems.add(path, "locked_type_editable");

  switch (type) {
    case "text": {
      const maxLength = own(field, "max_length");
      const maxLengthValid = isWholeNumber(maxLength) && maxLength >= 1 && maxLength <= TEXT_MAX_LENGTH;
      if (!maxLengthValid) problems.add(path, "max_length_invalid");
      if (
        Object.hasOwn(field, "allowed_values") &&
        !isValidAllowedValues(field.allowed_values, maxLengthValid ? maxLength : null)
      ) {
        problems.add(path, "allowed_values_invalid");
      }
      break;
    }
    case "number": {
      const min = own(field, "min");
      const max = own(field, "max");
      if (!isBound(min) || !isBound(max) || min > max) problems.add(path, "min_max_invalid");
      if (typeof own(field, "integer") !== "boolean") problems.add(path, "integer_invalid");
      break;
    }
    case "price": {
      const max = own(field, "max");
      if (!(typeof max === "number" && max > 0 && max <= PRICE_MAX && hasAtMostTwoDecimals(max))) {
        problems.add(path, "price_max_invalid");
      }
      if (!isPriceOption(own(field, "from")) || !isPriceOption(own(field, "incl_vat"))) {
        problems.add(path, "price_option_invalid");
      }
      break;
    }
    case "list": {
      const itemFields = own(field, "item_fields");
      if (!Array.isArray(itemFields) || itemFields.length === 0) problems.add(path, "item_fields_invalid");
      const minItems = own(field, "min_items");
      const maxItems = own(field, "max_items");
      const boundsValid =
        isWholeNumber(minItems) &&
        minItems >= 0 &&
        isWholeNumber(maxItems) &&
        maxItems >= 1 &&
        maxItems <= LIST_MAX_ITEMS &&
        minItems <= maxItems;
      if (!boundsValid) problems.add(path, "items_bounds_invalid");
      if (Array.isArray(itemFields)) {
        const itemKeys = new Set<string>();
        itemFields.forEach((itemField: unknown, index) => {
          const slot = `${path}.item_fields[${index}]`;
          checkField(itemField, slot, (itemKey) => `${path}.${itemKey}`, true, itemKeys, problems);
        });
        // A list whose items carry a phone, an email or a url is locked as a whole, since an
        // editable list would let the client change them through it.
        if (
          own(field, "editable") === true &&
          itemFields.some((itemField: unknown) => isObject(itemField) && isLockedType(own(itemField, "type")))
        ) {
          problems.add(path, "locked_type_editable");
        }
      }
      break;
    }
    case "opening_hours":
    case "phone":
    case "email":
    case "url":
      // No properties of their own; the unknown-property and lock checks above are all there is.
      break;
  }
}

/**
 * Every problem in a facts schema (`content/facts.schema.json`, parsed), in the order "Schema problem
 * codes" in `docs/facts-schema.md` gives. An empty list means the schema is valid; any problem
 * refuses it whole.
 */
export function validateFactsSchema(schema: unknown): FactsProblem[] {
  const problems = new ProblemList();
  if (!isObject(schema)) {
    problems.add("$", "schema_not_object");
    return problems.problems;
  }
  if (Object.keys(schema).some((name) => name !== "version" && name !== "fields")) {
    problems.add("$", "unknown_property");
  }
  if (own(schema, "version") !== 1) problems.add("$", "version_unsupported");

  const fields = own(schema, "fields");
  if (!Array.isArray(fields)) {
    problems.add("$", "fields_missing");
    return problems.problems;
  }
  if (fields.length === 0) problems.add("$", "fields_empty");

  const keys = new Set<string>();
  fields.forEach((field: unknown, index) =>
    checkField(field, `fields[${index}]`, (key) => key, false, keys, problems)
  );
  return problems.problems;
}

/** A price option the schema fixes binds every value: Petrovic's and, from round 19, the client's. */
function differsFromFixed(option: FactsPriceOption | undefined, actual: boolean): boolean {
  return option !== undefined && option.editable === false && option.value !== actual;
}

function checkText(field: AnyField, value: unknown, path: string, problems: ProblemList): void {
  if (typeof value !== "string") {
    problems.add(path, "wrong_type");
    return;
  }
  // Independent of each other: a value can be blank and outside the allowed values at once.
  if (hasControlCharacter(value)) problems.add(path, "control_character");
  if (isBlank(value)) problems.add(path, "blank");
  if (codePointLength(value) > (field.max_length ?? 0)) problems.add(path, "too_long");
  if (field.allowed_values !== undefined && !field.allowed_values.includes(value)) {
    problems.add(path, "not_allowed_value");
  }
}

function checkNumber(field: AnyField, value: unknown, path: string, problems: ProblemList): void {
  if (typeof value !== "number") {
    problems.add(path, "wrong_type");
    return;
  }
  if (field.integer === true && !Number.isInteger(value)) problems.add(path, "not_integer");
  if (value < (field.min ?? 0) || value > (field.max ?? 0)) problems.add(path, "out_of_range");
}

function checkPrice(field: AnyField, value: unknown, path: string, problems: ProblemList): void {
  if (!isObject(value) || Object.keys(value).length !== 3) {
    problems.add(path, "wrong_type");
    return;
  }
  const amount = own(value, "amount");
  const from = own(value, "from");
  const inclVat = own(value, "incl_vat");
  if (typeof amount !== "number" || typeof from !== "boolean" || typeof inclVat !== "boolean") {
    problems.add(path, "wrong_type");
    return;
  }
  if (amount < 0 || amount > (field.max ?? 0)) problems.add(path, "out_of_range");
  if (!hasAtMostTwoDecimals(amount)) problems.add(path, "too_many_decimals");
  if (differsFromFixed(field.from, from) || differsFromFixed(field.incl_vat, inclVat)) {
    problems.add(path, "price_fixed");
  }
}

function isTime(value: unknown): value is string {
  return typeof value === "string" && TIME_PATTERN.test(value);
}

function checkOpeningHours(value: unknown, path: string, problems: ProblemList): void {
  if (!Array.isArray(value) || value.length !== OPENING_DAYS.length) {
    problems.add(path, "opening_hours_invalid");
    return;
  }
  value.forEach((day: unknown, index) => {
    const dayPath = `${path}[${index}]`;
    // The shape first: exactly the four properties, the right day for the position, and a boolean
    // `closed`. A day that fails it is not read any further, because without a boolean `closed`
    // nothing says which of the two rules below applies.
    if (
      !isObject(day) ||
      Object.keys(day).length !== 4 ||
      !Object.hasOwn(day, "from") ||
      !Object.hasOwn(day, "to") ||
      own(day, "day") !== OPENING_DAYS[index] ||
      typeof own(day, "closed") !== "boolean"
    ) {
      problems.add(dayPath, "opening_hours_invalid");
      return;
    }
    if (day.closed === true) {
      if (day.from !== null || day.to !== null) problems.add(dayPath, "opening_hours_invalid");
      return;
    }
    if (!isTime(day.from) || !isTime(day.to)) problems.add(dayPath, "time_invalid");
    // Zero-padded `HH:MM` orders as text exactly as it orders as time.
    else if (day.from >= day.to) problems.add(dayPath, "time_order");
  });
}

function checkPatternString(
  value: unknown,
  path: string,
  problems: ProblemList,
  valid: (text: string) => boolean,
  code: "phone_invalid" | "email_invalid" | "url_invalid"
): void {
  if (typeof value !== "string") problems.add(path, "wrong_type");
  else if (!valid(value)) problems.add(path, code);
}

function isEmail(value: string): boolean {
  // The lengths first, so the pattern never runs over an arbitrarily long string.
  if (value.length > EMAIL_MAX || value.indexOf("@") > EMAIL_LOCAL_MAX) return false;
  return EMAIL_PATTERN.test(value);
}

function isUrl(value: string): boolean {
  return value.length <= URL_MAX && URL_PATTERN.test(value);
}

function checkList(field: FactsField, value: unknown, path: string, problems: ProblemList): void {
  if (!Array.isArray(value)) {
    problems.add(path, "wrong_type");
    return;
  }
  if (field.required && value.length === 0) problems.add(path, "required");
  if (value.length < (field.min_items ?? 0)) problems.add(path, "too_few_items");
  if (value.length > (field.max_items ?? 0)) problems.add(path, "too_many_items");

  const itemFields = field.item_fields ?? [];
  const itemKeys = new Set(itemFields.map((itemField) => itemField.key));
  value.forEach((item: unknown, index) => {
    const itemPath = `${path}[${index}]`;
    if (!isObject(item)) {
      problems.add(itemPath, "wrong_type");
      return;
    }
    for (const name of Object.keys(item)) {
      if (itemKeys.has(name)) continue;
      problems.add(KEY_PATTERN.test(name) ? `${itemPath}.${name}` : itemPath, "unknown_key");
    }
    for (const itemField of itemFields) {
      checkValue(itemField, own(item, itemField.key) ?? null, `${itemPath}.${itemField.key}`, problems);
    }
  });
}

function checkValue(field: AnyField, value: unknown, path: string, problems: ProblemList): void {
  if (value === null) {
    if (field.required) problems.add(path, "required");
    return;
  }
  switch (field.type) {
    case "text":
      checkText(field, value, path, problems);
      break;
    case "number":
      checkNumber(field, value, path, problems);
      break;
    case "price":
      checkPrice(field, value, path, problems);
      break;
    case "opening_hours":
      checkOpeningHours(value, path, problems);
      break;
    case "phone":
      checkPatternString(value, path, problems, (text) => PHONE_PATTERN.test(text), "phone_invalid");
      break;
    case "email":
      checkPatternString(value, path, problems, isEmail, "email_invalid");
      break;
    case "url":
      checkPatternString(value, path, problems, isUrl, "url_invalid");
      break;
    case "list":
      checkList(field, value, path, problems);
      break;
  }
}

/**
 * Every problem in a set of values (a draft or a version) against a schema, in the order "Value
 * problem codes" in `docs/facts-schema.md` gives. The schema must be one `validateFactsSchema`
 * accepts. The fallbacks for a property a valid schema always has (`?? 0`) only keep the types
 * honest, and they fail closed.
 */
export function validateFactsValues(schema: FactsSchema, values: unknown): FactsProblem[] {
  const problems = new ProblemList();
  if (!isObject(values)) {
    problems.add("$", "values_not_object");
    return problems.problems;
  }
  const keys = new Set(schema.fields.map((field) => field.key));
  for (const name of Object.keys(values)) {
    if (!keys.has(name)) problems.add(KEY_PATTERN.test(name) ? name : "$", "unknown_key");
  }
  // A missing key is the same as null.
  for (const field of schema.fields) checkValue(field, own(values, field.key) ?? null, field.key, problems);
  return problems.problems;
}

/**
 * A Swedish phone number as the contract stores it (`+46` and seven to nine digits), from the ways
 * people write one: `070-123 45 67`, `08-123 456 78`, `0046 8 123 456 78`, `+46 (0)8 123 456 78`.
 * Spaces, hyphens, en dashes and parentheses are separators. Null when the input is not a Swedish
 * number this can read with certainty: a foreign country code, a number without its leading 0, or
 * anything with a letter in it.
 */
export function normalisePhone(input: string): string | null {
  const compact = input.trim().replace(/[\s\u{2013}()-]/gu, "");
  if (!/^\+?[0-9]+$/.test(compact)) return null;

  // `+46 (0)8 ...` and `0046 08 ...` keep the trunk 0 after the country code; it goes like the
  // trunk 0 of `08 ...` does.
  let national: string;
  if (compact.startsWith("+46")) national = compact.slice(3).replace(/^0/, "");
  else if (compact.startsWith("0046")) national = compact.slice(4).replace(/^0/, "");
  else if (/^0[1-9]/.test(compact)) national = compact.slice(1);
  else return null;

  const normalised = `+46${national}`;
  return PHONE_PATTERN.test(normalised) ? normalised : null;
}
